/**
 * ============================================================
 * KEJJA OS — src/pages/calendar/WeekView.tsx
 * 7-day week view with events stacked per day column.
 * ============================================================
 */

import { useMemo } from 'react';
import { ChevronLeft, ChevronRight, Repeat, Bell, Plus } from 'lucide-react';
import { useCalendarContext } from './CalendarLayout';
import { useEvents } from '@/hooks/useCalendar';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

const DOW_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function mondayOf(d: Date): Date {
  const c = new Date(d);
  c.setUTCHours(0, 0, 0, 0);
  const dow = c.getUTCDay();
  const offset = dow === 0 ? -6 : 1 - dow;
  c.setUTCDate(c.getUTCDate() + offset);
  return c;
}

export default function WeekView() {
  const {
    homeId, cursorDate, setCursorDate,
    visibleCalendarIds, routinesOn, openNewEvent,
  } = useCalendarContext();

  const weekStart = useMemo(() => mondayOf(cursorDate), [cursorDate]);

  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }).map((_, i) => {
      const d = new Date(weekStart);
      d.setUTCDate(weekStart.getUTCDate() + i);
      return d;
    });
  }, [weekStart]);

  const fromStr = weekDays[0]!.toISOString().slice(0, 10);
  const toStr = weekDays[6]!.toISOString().slice(0, 10);

  const { data: eventsData, isLoading } = useEvents(
    homeId,
    visibleCalendarIds.size > 0
      ? { from: fromStr, to: toStr, calendarIds: Array.from(visibleCalendarIds), includeRoutines: routinesOn }
      : null,
  );

  const prevWeek = () => {
    const d = new Date(cursorDate);
    d.setUTCDate(d.getUTCDate() - 7);
    setCursorDate(d);
  };
  const nextWeek = () => {
    const d = new Date(cursorDate);
    d.setUTCDate(d.getUTCDate() + 7);
    setCursorDate(d);
  };
  const today = () => {
    const d = new Date();
    d.setUTCHours(0, 0, 0, 0);
    setCursorDate(d);
  };

  const todayStr = new Date().toISOString().slice(0, 10);

  const weekLabel =
    weekDays[0]!.toLocaleDateString('en-KE', { month: 'short', day: 'numeric' })
    + ' – ' +
    weekDays[6]!.toLocaleDateString('en-KE', { month: 'short', day: 'numeric' });

  return (
    <div className="space-y-4 pb-8">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={prevWeek}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="font-semibold min-w-[150px] text-center text-sm">
            {weekLabel}
          </div>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={nextWeek}>
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" className="text-xs ml-1" onClick={today}>
            Today
          </Button>
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="h-96" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-7 gap-2">
          {weekDays.map((day) => {
            const dateStr = day.toISOString().slice(0, 10);
            const isToday = dateStr === todayStr;
            const dayEvents = (eventsData?.events ?? []).filter((e) => e.occurrenceDate === dateStr);
            const dayRoutines = (eventsData?.routines ?? []).filter((r) => r.occurrenceDate === dateStr);
            const dow = (day.getUTCDay() + 6) % 7;     // 0=Mon..6=Sun

            return (
              <Card key={dateStr} className={cn('overflow-hidden', isToday && 'ring-1 ring-indigo-500/40')}>
                <CardContent className="p-0">
                  <button
                    onClick={() => openNewEvent({ date: dateStr })}
                    className={cn(
                      'w-full px-3 py-2 text-left border-b border-border transition-colors flex items-center justify-between',
                      isToday ? 'bg-indigo-500/10' : 'bg-muted/30',
                    )}
                  >
                    <div>
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {DOW_LABELS[dow]}
                      </div>
                      <div className={cn(
                        'text-lg font-bold tabular-nums',
                        isToday && 'text-indigo-600 dark:text-indigo-400',
                      )}>
                        {day.getUTCDate()}
                      </div>
                    </div>
                    <Plus className="w-3.5 h-3.5 text-muted-foreground/50" />
                  </button>

                  <div className="p-2 space-y-1.5 min-h-[100px]">
                    {dayEvents.map((e) => {
                      const color = e.color ?? e.calendarColor;
                      return (
                        <div
                          key={e.id + e.occurrenceDate}
                          className="rounded-md p-1.5 text-xs border-l-2"
                          style={{
                            borderLeftColor: color,
                            backgroundColor: `${color}14`,
                          }}
                        >
                          <div className="font-medium truncate flex items-center gap-1">
                            {e.isReminder && <Bell className="w-2.5 h-2.5 flex-shrink-0" />}
                            {e.title}
                          </div>
                          {!e.allDay && (
                            <div className="text-[10px] text-muted-foreground tabular-nums">
                              {new Date(e.startsAt).toISOString().slice(11, 16)}
                              {e.endsAt && ` – ${new Date(e.endsAt).toISOString().slice(11, 16)}`}
                            </div>
                          )}
                          {e.location && (
                            <div className="text-[10px] text-muted-foreground truncate">📍 {e.location}</div>
                          )}
                        </div>
                      );
                    })}
                    {dayRoutines.length > 0 && (
                      <div className="border-t border-dashed border-border pt-1.5 space-y-1">
                        {dayRoutines.map((r) => (
                          <div
                            key={r.id + r.occurrenceDate}
                            className="text-[11px] flex items-center gap-1 px-1.5 py-0.5 rounded text-teal-700 dark:text-teal-300 bg-teal-500/10"
                          >
                            <Repeat className="w-2.5 h-2.5 flex-shrink-0" />
                            {r.icon && <span>{r.icon}</span>}
                            <span className="truncate">{r.title}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {dayEvents.length === 0 && dayRoutines.length === 0 && (
                      <div className="text-[10px] text-muted-foreground/60 italic pt-6 text-center">
                        Nothing planned
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
